import { HttpClientModule, HttpClient } from '@angular/common/http';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { faLock, faUser } from '@fortawesome/pro-solid-svg-icons';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@s8l/client-tree-lib';
import { ChartsModule } from 'ng2-charts';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ToastrModule } from 'ngx-toastr';

import { environment, fetchConfig } from 'src/environments/environment';

import { AppInitService } from './services/app-init.service';
import { UserServerService } from './services/jsonrpc/user-server.service';

/**
 * Combined initializer: fetch config then restore auth state.
 * This ensures environment is loaded before attempting to restore auth.
 */
export function initializeApp(http: HttpClient, userServer: UserServerService, auth: AuthService, appInit: AppInitService) {
  return () => {
    console.log('[App Init] Loading environment configuration...');

    // First: fetch config and load environment
    return fetchConfig(http)()
      .then(() => {
        console.log('[App Init] Environment loaded successfully');
        console.log('[Auth Init] Attempting to restore auth state from cookie...');

        // Then: try to restore auth state from HttpOnly cookie
        return userServer.accountRefresh();
      })
      .then(response => {
        console.log('[Auth Init] Refresh successful:', response);
        if (response?.profile) {
          auth.auth(response.profile);
          console.log('[Auth Init] Auth state restored, isAuth:', auth.isAuth);
        }
      })
      .catch(err => {
        console.log('[Auth Init] Refresh failed (expected if not logged in):', err);
        // Cookie invalid/expired or user not authenticated - expected for logged-out users
        // No action needed, user will be redirected to login by route guard
      })
      .finally(() => {
        // Mark initialization complete to hide loading screen
        appInit.markInitialized();
        console.log('[App Init] Initialization complete');
      });
  };
}

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AccessControlModalComponent } from './components/access-modal/access-modal.component';
import { ColumnsFilterModalComponent } from './components/columns-filter-modal/columns-filter-modal.component';
import { ComponentsModule } from './components/components.module';
import { DurationComponent } from './components/duration-picker/duration-picker.component';
import { ShopifyLicenceModalComponent } from './components/shopify-licence-modal/shopify-licence-modal.component';
import { ShopifyPurchaseModalComponent } from './components/shopify-purchase-modal/shopify-purchase-modal.component';
import { UserModalComponent } from './components/user-modal/user-modal.component';
import { UserMultiModalComponent } from './components/user-multi-modal/user-multi-modal.component';
import { UserPageViewComponent } from './components/user-page-view/user-page-view.component';
import { AdminComponent } from './layout/admin/admin.component';
import { AuthComponent } from './layout/auth/auth.component';
import { ConfigurationModule } from './modules/configuration/configuration.module';
import { AchievementComponent } from './pages/achievement/achievement.component';
import { AchievementEditComponent } from './pages/achievement/edit/achievement-edit.component';
import { DashboardComponent } from './pages/admin/dashboard/dashboard.component';
import { DashboardAccessControlComponent } from './pages/admin/dashboard-access-control/dashboard-access-control.component';
import { EventsComponent } from './pages/admin/events/events.component';
import { LicenceComponent } from './pages/admin/licence/licence.component';
import { RegisteredUsersComponent } from './pages/admin/registered-users/registered-users.component';
import { RolesComponent } from './pages/admin/roles/roles.component';
import { ShopifyComponent } from './pages/admin/shopify/shopify.component';
import { WhitelistComponent } from './pages/admin/whitelist/whitelist.component';
import { ReportsComponent } from './pages/admin/reports/reports.component';
import { AuditUserEventsComponent } from './pages/audit-user-events/audit-user-events.component';
import { ChatComponent } from './pages/chat/chat.component';
import { ConfigurationComponent } from './pages/configuration/configuration.component';
import { HomeComponent } from './pages/home/home.component';
import { JourneyEditComponent } from './pages/journey/edit/journey-edit.component';
import { JourneyComponent } from './pages/journey/journey.component';
import { LoginComponent } from './pages/login/login.component';
import { NodeComponent } from './pages/node/node.component';
import { PermissionComponent } from './pages/permission/permission.component';
import { PermissionsGuideComponent } from './pages/help/permissions-guide/permissions-guide.component';
import { SurveyEditComponent } from './pages/survey/edit/edit.component';
import { SurveyResultComponent } from './pages/survey/result/result.component';
import { SurveyComponent } from './pages/survey/survey.component';
import { VimeoComponent } from './pages/vimeo/vimeo.component';
import { GlobalErrorHandler } from './services/errorhandler.service';
import { VRSpeechComponent } from './pages/admin/vrspeech/vrspeech.component';
import { VRSpeechTrainerModalComponent } from './components/vrspeech-trainer-modal/vrspeech-trainer-modal.component';

@NgModule({
  declarations: [
    AppComponent,
    AdminComponent,
    AuthComponent,
    HomeComponent,
    LoginComponent,
    NodeComponent,
    PermissionComponent,
    PermissionsGuideComponent,
    AchievementComponent,
    AchievementEditComponent,
    JourneyComponent,
    JourneyEditComponent,
    ConfigurationComponent,
    ChatComponent,
    VimeoComponent,
    SurveyComponent,
    SurveyEditComponent,
    SurveyResultComponent,
    AuditUserEventsComponent,
    DashboardComponent,
    DashboardAccessControlComponent,
    RegisteredUsersComponent,
    WhitelistComponent,
    RolesComponent,
    UserModalComponent,
    UserPageViewComponent,
    ColumnsFilterModalComponent,
    UserMultiModalComponent,
    DurationComponent,
    LicenceComponent,
    AccessControlModalComponent,
    ShopifyComponent,
    EventsComponent,
    ReportsComponent,
    ShopifyLicenceModalComponent,
    ShopifyPurchaseModalComponent,
    VRSpeechComponent,
    VRSpeechTrainerModalComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    NgbModule,
    ChartsModule,
    FontAwesomeModule,
    ToastrModule.forRoot(),
    ComponentsModule,
    InfiniteScrollModule,
    ConfigurationModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
      deps: [HttpClient, UserServerService, AuthService, AppInitService]
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIconPacks(fal);
    library.addIcons(faUser, faLock);
  }
}

declare global {
  interface Navigator {
    msSaveBlob: (blob: Blob, fileName: string) => boolean;
  }
}
